import React from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import HelpSidebar from './HelpSidebar';
import AskAardvarkHelpHint from './AskAardvarkHelpHint';

interface DashboardHelpSidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
  viewType?: 'my' | 'team' | 'organisation';
}

const DASHBOARD_HELP_TITLES: Record<'my' | 'team' | 'organisation', string> = {
  my: 'My Dashboard Help',
  team: 'Team Dashboard Help',
  organisation: 'Organisation Dashboard Help',
};

const DASHBOARD_ASK_ABOUT: Record<'my' | 'team' | 'organisation', string> = {
  my: 'your tasks, leave, and check-ins',
  team: 'team leave, development plans, and check-ins',
  organisation: 'organisation reviews, development plans, check-ins, and statutory leave',
};

export const DashboardHelpSidebar: React.FC<DashboardHelpSidebarProps> = ({
  isExpanded,
  onToggle,
  viewType = 'my',
}) => {
  const { user } = useAuth();
  const userRoles = user?.roles || [];
  const isTeamLeadOnly = userRoles.includes('TeamLead') && !userRoles.some((r: string) => r === 'Manager' || r === 'SeniorManager' || r === 'Administrator');

  const renderMyDashboardHelp = () => (
    <>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
        This is your <strong>personal dashboard</strong> showing your own reviews, check-ins, development plan, and leave at a glance. <strong>What we value</strong> is first, then warning banners when they apply (e.g. outstanding tasks, open disputes for administrators), then <strong>Personal milestones</strong> when HR dates are on file (years of service, birthday and work anniversary countdowns; informational only, not extra email notifications). On your birthday or work anniversary, those cards switch to a more celebratory style for the day.
      </Typography>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        OUTSTANDING TASKS
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Warning banner"
            secondary="When you have outstanding tasks (e.g. self-review, manager review, peer feedback you can still submit, peers outstanding on reviews you manage, check-ins), a warning banner appears directly under the What we value section (and above Personal milestones when that section is shown), with the count and a 'View my tasks' button. Click it to open the Tasks page and work through your list. Self-review tasks disappear once the **employee cutoff** has passed for that cycle; the review page also blocks save and submit after the cutoff (the cutoff day itself is still allowed). Peer feedback tasks disappear once the review no longer accepts peer submissions (e.g. after the manager submits for discussion or finalises)."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Who appears on Tasks"
            secondary="Tasks is your personal to-do list, not a team or org hierarchy view (use Team / Organisation pages for that). Users with the **ReportingExempt** role are not prompted with a self-review task on Tasks. **Intern** users are in the normal review workflow (self-review and related tasks apply). Manager items on Tasks (reviews, leave approvals, expenses, IDPs, etc.) are for your direct reports only. Team Leave and the Team Dashboard statutory leave tile default to **direct reports** for users with the Manager role (aligned with approvals); use the Team filter on Team Leave to include everyone in your reporting line. Team Leads see direct reports only. Check-in tasks that ask you to record on-time / late / missed only appear when you are the assigned manager for that check-in—not for every overdue check-in in your reporting line. After you record a decision, that check-in leaves **Record decision** on Tasks even if your report still needs to acknowledge (their **Acknowledge occurrence** task, not yours). Senior (second-step) leave approvals appear on Team Leave, not on Tasks."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="All caught up"
            secondary="When you have no outstanding tasks, the Tasks page shows 'You're all caught up!' with a clear empty state so you know there's nothing that needs your action."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      </List>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        REVIEW DISPUTES (ADMINISTRATORS)
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Banner and sidebar badge"
            secondary="If you have the Administrator role and there are open review disputes, a warning banner appears at the top of My Dashboard with the count and a 'Manage disputes' button. The Disputes item in the admin section of the sidebar also shows a red badge with the same count. Counts refresh when you open Dispute Management."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      </List>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        REVIEWS
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Loading"
            secondary="A loading bar appears at the top of the dashboard view while data is loading."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Current Cycle"
            secondary="Shows the active review cycle name, period, employee cutoff, and days remaining. Click the card to navigate to the review cycles page."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Completion Rate"
            secondary={
              <>
                Your personal review progress for <strong>active cycle(s) only</strong>, using staged progress: Not started 0%, In progress (draft saved) 25%, Self-review submitted 50%, Awaiting discussion or finalisation 75%, Finalised (or discussion acknowledged / excluded) 100%. The percentage is the average across your reviews, so you see progress as soon as you start or submit. The dashboard refreshes when you return to this tab.
              </>
            }
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2', component: 'div' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Current Review Status"
            secondary="Shows your pending reviews with cycle name and status. Click any review to open it, or use View All to go to your full reviews list."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      </List>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        CHECK-INS
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Next check-in"
            secondary="Shows your next scheduled check-in: manager name and due date (or 'No upcoming check-in'). Click the card to open My Check-Ins where you can view details and acknowledge after the conversation. If your manager has turned off built-in check-ins, you will see that they manage ongoing 1:1s off-system instead of a scheduled frequency."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      </List>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        INDIVIDUAL DEVELOPMENT PLANS
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Development Plan"
            secondary="Shows your current Individual Development Plan (IDP) for the fiscal or calendar year, including status and number of development goals. Click to view; if you have none, use Create IDP to start one."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      </List>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        LEAVE
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Statutory leave to take"
            secondary="Only shown when you have not yet met the statutory minimum (15 days annual leave) and you are at risk or high risk (time-aware). When there is plenty of time left in the year, the card is hidden. At risk = time to plan; High risk = less than one month left. Click to book leave."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Annual leave"
            secondary="Shows how many days leave you have taken this year. Click the card to open your full leave page."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      </List>
    </>
  );

  const renderTeamDashboardHelp = () => (
    <>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
        This is your <strong>team management dashboard</strong> showing reviews and check-ins you need to manage.
        <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.85rem' }}>
          • <strong>Team Leads:</strong> See only direct reports (no hierarchical view)
          <br />
          • <strong>Managers/Senior Managers/Admins:</strong> See all employees in your hierarchy (through team leads down to employees)
          <br />
          • <strong>Sidebar:</strong> The <strong>Me</strong> section at the top has Dashboard, Tasks (with badge), Profile, and other personal links. Team Dashboard, My Team, and Team Leave appear under the <strong>Team</strong> section when you have direct reports (including admins who manage people).
        </Box>
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Section order on this dashboard is: <strong>Reviews</strong>, <strong>Individual Development Plans</strong>, <strong>Leave</strong>, then <strong>Check-Ins</strong>.
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        REVIEWS
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Loading"
            secondary="A loading bar appears at the top of the dashboard view while data is loading."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Review cycle filter"
            secondary="The dropdown lists all review cycles (active and closed). When there are active cycles, the first option is 'All active cycles'; you can also select a single cycle (including past/closed ones) to scope all metrics to that cycle. Closed cycles are labelled '(Closed)' in the list."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Current Cycle"
            secondary="Shows the active review cycle name, period, employee cutoff, and days remaining. If there are multiple active cycles, the card shows the first and the list below shows all. Click to navigate to the review cycles page."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Awaiting my review"
            secondary={
              <>
                Count of pending reviews for your direct reports that require your action, scoped to the selected cycle when one is chosen. Clicking the card opens the <strong>Reviews to Manage</strong> page with the same cycle pre-selected in the dropdown so you land in the right context immediately.
              </>
            }
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2', component: 'div' }}
          />
        </ListItem>
        {/* Your team and their teams help - only show for Managers/Senior Managers/Admins (not Team Leads) */}
        {!isTeamLeadOnly && (
          <ListItem sx={{ px: 0, py: 0.5 }}>
            <ListItemText
              primary="Your team and their teams"
              secondary={
                <>
                  <strong>Total number of people</strong> (reviews) in your hierarchy for the selected review cycle—your direct reports and their teams. This is headcount in scope for that cycle, not the count of pending reviews. View-only.
                  <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                    • When you select a cycle in the Review cycle dropdown, the number reflects that cycle only
                    <br />
                    • Clicking the card opens the full list with the same cycle pre-selected, so you land in the right context immediately
                    <br />
                    • The list page header shows how many reviews are listed (e.g. "4 reviews")
                    <br />
                    • Team Reviews also includes department analytics: average combined rating (finalised only), trend vs previous cycle, and final rating distribution
                    <br />
                    • Use "View reviews" from the department analytics table to auto-apply a department filter and jump to the filter area
                    <br />
                    • You can see status but don't manage these reviews directly
                  </Box>
                </>
              }
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
              secondaryTypographyProps={{ variant: 'body2', component: 'div' }}
            />
          </ListItem>
        )}
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Team Completion"
            secondary={
              <>
                Your direct reports' review progress for the <strong>selected cycle</strong> (or active cycle(s)), using the same staged scale: Not started 0%, In progress 25%, Self-review submitted 50%, Awaiting discussion/finalisation 75%, Finalised 100%. The percentage is the average across your team's reviews. Company completion rate (same scale) is shown below the bar for comparison.
                <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                  • Company completion rate shown below your team's progress bar (for Team Leads, Managers, Senior Managers, and Admins)
                  <br />
                  • The dashboard refreshes when you return to this tab
                </Box>
              </>
            }
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2', component: 'div' }}
          />
        </ListItem>
      </List>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        CHECK-INS
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Next check-in"
            secondary="Shows the single next team check-in (employee name and due date), or 'No upcoming check-in'. Priority: overdue first, then needing acknowledgment, then soonest scheduled. Click the card to open the Check-Ins page to manage team check-ins."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      </List>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        INDIVIDUAL DEVELOPMENT PLANS
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Total IDPs"
            secondary="Total number of IDPs for the current fiscal year for your team members. Click the card to view all team IDPs."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Outstanding"
            secondary="Number of direct reports without an Acknowledged IDP for the current fiscal year. Click the card to view team IDPs filtered to outstanding."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Not Submitted"
            secondary="Number of direct reports with no IDP or an IDP in Draft for the current fiscal year. Click the card to view team IDPs filtered to draft."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="With me"
            secondary="Number of IDPs submitted by your team members that are with you for approval. Click the card to view pending approvals."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Viewing Team IDPs"
            secondary={
              <>
                Each IDP card navigates to the team IDP list with the appropriate filter (Total, Outstanding, Not Submitted, or With me). Use the status filter on the list page to view Acknowledged or other statuses.
                <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                  • The page header shows how many IDPs are listed (e.g. "5 IDPs")
                  <br />
                  • Team IDP list shows employee name, status, year, and actions to view details
                  <br />
                  • Use search and status filters to find specific IDPs or filter by status
                  <br />
                  • Navigate to individual IDP pages to review and approve submitted IDPs
                  <br />
                  • Back button returns you to the dashboard
                </Box>
              </>
            }
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2', component: 'div' }}
          />
        </ListItem>
      </List>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        LEAVE
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Statutory leave to take"
            secondary="Uses the same default scope as Team Leave: Managers count direct reports (use the Team filter on Team Leave to include your full reporting line); Team Leads count direct reports only. Shows how many have not yet taken the statutory minimum annual leave (15 days) this year. Click the card to open Team Leave for the full breakdown."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      </List>


    </>
  );

  const renderOrganizationDashboardHelp = () => (
    <>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
        This is the <strong>organisation-wide dashboard</strong> showing reviews, IDP status, check-in compliance, and leave at risk across all departments.
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Section order on this dashboard is: <strong>Reviews</strong>, <strong>Individual Development Plans</strong>, <strong>Leave</strong>, then <strong>Check-Ins</strong>.
      </Typography>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        REVIEWS
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Loading"
            secondary="A loading bar appears at the top of the dashboard view while data is loading."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Review cycle filter"
            secondary="The dropdown lists all review cycles (active and closed). When there are active cycles, the first option is 'All active cycles'; you can also select a single cycle (including past/closed ones) to scope all metrics to that cycle. Closed cycles are labelled '(Closed)' in the list."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Current Cycle"
            secondary="Shows the active review cycle name and days remaining. Administrators also see **View outstanding tasks**, which opens the cycle chase list (self-reviews, manager reviews, and peer feedback pending with emails)."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Completion Rate"
            secondary="Company-wide progress for the selected cycle (or all active cycles). Uses the same staged scale as your personal completion: Not started 0%, In progress 25%, Self-review submitted 50%, Awaiting discussion/finalisation 75%, Finalised 100%. The percentage is the average across reviews in scope for reporting, so the rate moves through the scale as reviews progress. Shows number of departments tracked. Intern and ReportingExempt users are excluded from these organisation review metrics (and from org review lists); they may still have active reviews for their own workflow."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Pending Reviews"
            secondary="Total number of reviews across the organisation that are pending and requiring action, scoped to the selected cycle, excluding Intern and ReportingExempt from reporting counts. Click to navigate to the filtered reviews list."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Active Disputes"
            secondary={
              <>
                Number of reviews currently under HR dispute. The card only appears when there is at least one dispute.
                <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                  • <Chip label="Amber" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} /> 1–2 disputes (or oldest ≤14 days)
                  <br />
                  • <Chip label="Red" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} /> 3+ disputes or oldest &gt;14 days
                  <br />
                  • Shows age of oldest dispute
                  <br />
                  • Click to navigate to disputes page
                </Box>
              </>
            }
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2', component: 'div' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Managers that might need coaching"
            secondary="When employees acknowledge their review, they attest to how much feedback they received during the cycle (regular / some / little to none). This card and accordion list managers whose direct reports indicated limited or little feedback. Use it to prioritise coaching; managers do not see what was said. Click «View review» to open the review and see the full employee comment (visible to admins and senior managers only)."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Manager review load"
            secondary="Card and accordion showing manager span of control using headcount only. RAG thresholds: Green = 6 or fewer, Amber = 7-9, Red = 10 or more. Use this to quickly identify potential manager overload in intensive review cycles."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Status by Department"
            secondary={
              <>
                Collapsible accordion showing detailed review status by department with search, sort, and pagination. Counts exclude <strong>Intern</strong> and <strong>ReportingExempt</strong> employees from organisation review reporting.
                <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                  • Columns: Not Started, In Progress, Pending, Disputed, Excluded, Complete (count and completion %). The <strong>Complete %</strong> uses the same staged scale as the company card (Not started 0%, In progress 25%, Self-review submitted 50%, Awaiting discussion/finalisation 75%, Finalised 100%).
                  <br />
                  • <strong>RAG status on Complete:</strong> <Chip label="Green" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} /> 80%+ (on track), <Chip label="Amber" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} /> 25–79% (in progress), <Chip label="Red" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} /> &lt;25% (needs attention). Hover the dot for details.
                  <br />
                  • <strong>Leaderboard:</strong> Table opens sorted by completion % (best at top). Click the Complete column header to re-sort.
                  <br />
                  • Search by department name; click any column header to sort; use pagination (5, 10, 25, 50 rows per page).
                  <br />
                  • Click a department row to filter All Reviews by that department; click a disputed count to filter by department and dispute status.
                </Box>
              </>
            }
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2', component: 'div' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="All Reviews analytics (drill-down page)"
            secondary={
              <>
                The All Reviews page now includes a <strong>Department Average Rating</strong> section (finalised-only combined averages), cycle selector, trend deltas, and a <strong>Final Rating Distribution</strong> chart.
                <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                  • Trend label updates automatically: "latest vs prior" for All cycles, or "vs previous cycle" for a selected cycle
                  <br />
                  • Insight callouts summarise movement and most common outcome band
                  <br />
                  • Click "View reviews" or "Largest gain" to apply department filter and jump to the filter controls
                </Box>
              </>
            }
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2', component: 'div' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="All Reviews extract (Claude)"
            secondary="Administrators and senior managers can copy a starter from Ask Aardvark in the top bar and paste it in Claude for a table of everyone still at the company, with Q1–Q4 final results and each measure rating for a calendar year. Senior managers see only their reporting line. This extract is not a separate page in the app."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      </List>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        INDIVIDUAL DEVELOPMENT PLANS
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Total IDPs"
            secondary="Total number of Individual Development Plans across the organisation. Click to open All IDPs. ReportingExempt users are excluded from org IDP stats and lists; Intern users are included (unlike organisation review reporting)."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Outstanding"
            secondary="Number of employees without an Acknowledged IDP for the current fiscal year (population excludes ReportingExempt only). Click to open All IDPs filtered to outstanding (Draft or With manager)."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Not Submitted"
            secondary="Number of employees with no IDP or an IDP in Draft for the current fiscal year. Click to open All IDPs filtered to No IDP."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Acknowledged IDPs"
            secondary="Number of IDPs that have been acknowledged by managers and are currently active. Click to open All IDPs filtered to acknowledged."
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Status by Department"
            secondary={
              <>
                Collapsible accordion showing IDP counts by department with search, sort, and pagination:
                <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                  • Columns: Department, Total, With manager, Acknowledged, Outstanding %
                  <br />
                  • <strong>RAG status on Outstanding %:</strong> <Chip label="Green" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} /> &lt;20% outstanding (on track), <Chip label="Amber" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} /> 20–79% (in progress), <Chip label="Red" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} /> 80%+ (needs attention). Hover the dot for details.
                  <br />
                  • <strong>Leaderboard:</strong> Table opens sorted by Outstanding % (best at top). Click the Outstanding % column header to re-sort.
                  <br />
                  • Search by department name; click any column header to sort; pagination (5, 10, 25, 50 rows per page).
                  <br />
                  • Click a department row or summary card to open the All IDPs page (with filters applied).
                  <br />
                  • The All IDPs page header shows how many IDPs are listed (e.g. "12 IDPs").
                  <br />
                  • Click a department's pending count to navigate to pending IDP approvals.
                </Box>
              </>
            }
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2', component: 'div' }}
          />
        </ListItem>
      </List>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        CHECK-INS
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Overdue, Late, Missed cards"
            secondary={
              <>
                Three summary cards showing organisation-wide totals for managers who still use built-in check-ins:
                <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                  • <strong>Overdue:</strong> Check-ins past due date, awaiting manager decision (orange card)
                  <br />
                  • <strong>Late:</strong> Check-ins marked as happened after the deadline (blue/teal card)
                  <br />
                  • <strong>Missed:</strong> Check-ins marked as did not happen (pink/coral card)
                  <br />
                  • Managers who turned off built-in check-ins appear as <strong>Opted out — off-system</strong> and are not counted as incomplete setup
                </Box>
              </>
            }
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2', component: 'div' }}
          />
        </ListItem>
      </List>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
        LEAVE
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="Statutory leave at risk"
            secondary={
              <>
                Summary cards for employees below their country statutory annual target (time-aware RAG). Separate counts for <strong>South Africa</strong> (15 days), <strong>United Kingdom</strong> (28 days), and <strong>India</strong> (15 days). Employees who have met target are excluded.
                <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                  • Click a card to open Organisation Leave filtered by that country
                  <br />
                  • Use this to proactively identify employees who may need to be encouraged to take leave before year end
                </Box>
              </>
            }
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2', component: 'div' }}
          />
        </ListItem>
        <ListItem sx={{ px: 0, py: 0.5 }}>
          <ListItemText
            primary="By Employee"
            secondary={
              <>
                Collapsible accordion listing each at-risk employee with their current annual leave taken and days remaining to the statutory minimum:
                <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                  • Search by employee name; click column headers to sort; pagination (5, 10, 25, 50 rows per page)
                  <br />
                  • "Days to take" column shows how many more days are needed to meet the statutory minimum
                </Box>
              </>
            }
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'body2', component: 'div' }}
          />
        </ListItem>
      </List>
    </>
  );

  const title = DASHBOARD_HELP_TITLES[viewType];
  const content =
    viewType === 'my'
      ? renderMyDashboardHelp()
      : viewType === 'team'
        ? renderTeamDashboardHelp()
        : renderOrganizationDashboardHelp();

  return (
    <HelpSidebar isExpanded={isExpanded} onToggle={onToggle} title={title}>
      {content}
      <AskAardvarkHelpHint about={DASHBOARD_ASK_ABOUT[viewType]} />
    </HelpSidebar>
  );
};

export default DashboardHelpSidebar;
